/**
 * Access feed — GET /api/v1/control/logs, tenant-scoped by X-Company-Id, plus the
 * spreadsheet export and the history wipe that share its filters.
 *
 * `period` is intentionally not exposed: sv_control does not thread it through (the
 * gateway always sends ListLogsRequest.period = None), so any time window is an
 * offset/client-side concern, not a server filter. Everything else the route accepts
 * IS exposed — device, department and a minimum-similarity floor were missing here
 * long after the server grew them, which is why the feed could not answer "what did
 * this one terminal let through at a poor match".
 */
import {requestControl, requestControlRaw} from '@/shared/api';
import type {LogResultFilter, PagedLogs} from '@/entities/log/types';

export type LogFilters = {
  result?: LogResultFilter;
  recordId?: string;
  search?: string;
  deviceId?: string;
  departmentId?: string;
  /** Only events at or above this match quality, 0..100. */
  minSimilarityPercent?: number;
};

export type FetchLogsParams = LogFilters & {
  limit: number;
  offset: number;
};

/** The filter half of the query — shared verbatim by the list and the export. */
function filterParams(filters: LogFilters): URLSearchParams {
  const qs = new URLSearchParams();
  if (filters.result) {
    qs.set('result', filters.result);
  }
  if (filters.recordId) {
    qs.set('record_id', filters.recordId);
  }
  if (filters.search) {
    qs.set('search', filters.search);
  }
  if (filters.deviceId) {
    qs.set('device_id', filters.deviceId);
  }
  if (filters.departmentId) {
    qs.set('department_id', filters.departmentId);
  }
  if (filters.minSimilarityPercent !== undefined) {
    qs.set('min_similarity_percent', String(filters.minSimilarityPercent));
  }
  return qs;
}

export function fetchLogs(
  companyId: string,
  params: FetchLogsParams,
): Promise<PagedLogs> {
  const qs = filterParams(params);
  qs.set('limit', String(params.limit));
  qs.set('offset', String(params.offset));
  return requestControl<PagedLogs>(`/control/logs?${qs.toString()}`, companyId);
}

/**
 * The same timeline as a file. Takes the filters but NOT limit/offset — an export is
 * the whole matching set by definition, which is also why it is the one place a long
 * period is allowed.
 */
export function exportLogs(
  companyId: string,
  filters: LogFilters = {},
  format: 'xlsx' | 'csv' = 'xlsx',
  /** Язык заголовков листа. Отчёт читает тот же, кто его просил. */
  lang?: string,
): Promise<Response> {
  const qs = filterParams(filters);
  qs.set('format', format);
  if (lang) {
    qs.set('lang', lang);
  }
  return requestControlRaw(`/control/logs/export?${qs.toString()}`, companyId);
}

/**
 * DELETE /control/logs — erase this company's access history.
 *
 * Irreversible, unfiltered (it takes no filters at all: it is the whole history or
 * nothing), and gated by a password the company sets on the terminal, NOT by the
 * caller's own account password. `GET /control/settings/security` returns the hint
 * for it — see {@link fetchSecuritySettings} in entities/settings.
 */
export function clearLogs(companyId: string, password: string): Promise<void> {
  return requestControl<void>('/control/logs', companyId, {
    method: 'DELETE',
    body: JSON.stringify({password}),
  });
}
